"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/features/auth/context/AuthContext";
import { getTalentsApplicationsByUserRole } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { Profile } from "../../talent/types";
import BaseTalentsList from "./BaseTalentsList";
import { AddNewTalentButton } from "@/features/common/AddNewTalentButton";

export default function SuperUserTalentsList() {
  const [talentProfiles, setTalentProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();
  const { t } = useTranslation(["applications"]);

  const fetchTalentProfiles = useCallback(async () => {
    try {
      if (profile.anonymous) {
        console.error("No user found");
        throw new Error("No user found");
      }
      const profiles = await getTalentsApplicationsByUserRole(
        profile.id,
        "superuser"
      );
      setTalentProfiles(profiles);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (!profile.anonymous) {
      fetchTalentProfiles();
    }
  }, [profile, t, fetchTalentProfiles]);

  return (
    <div className="flex h-screen bg-white">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold">
                {t("titles.all_talent_profiles_superuser")}
              </h1>
              <p className="text-gray-500">
                {t("descriptions.all_talent_profiles_superuser")}
              </p>
            </div>
            <AddNewTalentButton
              isApplicationList={false}
              onCreated={fetchTalentProfiles}
            />
          </div>
        </div>

        {/* Content */}
        <BaseTalentsList
          talentProfiles={talentProfiles}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
}
